import {Injectable} from '@angular/core'

import {Observable, zip} from 'rxjs'
import {map, filter} from 'rxjs/operators'

import {GenericManagerService} from '../generic-manager.service'
import {UserService, User, Role} from '@app/core/services/user/user.service'
import {NotificationService} from '@app/core/services/notification.service'
import {LoggerService} from '@app/core/services/logger.service'

/**
 * Whether Keycloak owns this account's profile.
 *
 * Read straight off the API payload, so it is deliberately lenient about how
 * the backend spells and types the flag: this API serialises booleans as
 * 'true'/'false' strings in places (see `UserDataService.isForcePushDisabled`)
 * and the portal has no schema to pin the casing to. Anything absent reads as
 * not migrated, keeping the pre-existing "send both requests" path for every
 * account the backend does not mark.
 */
export function isKeycloakMigrated(user: User): boolean {
  const raw = user as unknown as Record<string, unknown>
  const flag = raw['keycloakMigrated'] ?? raw['keycloakmigrated']
  return flag === true || flag === 'true'
}

@Injectable()
export class UserManagerService extends GenericManagerService<User> {
  domain

  constructor(
    private userSvc: UserService,
    protected notify: NotificationService,
    protected logger: LoggerService,
  ) {
    super(notify, logger)
  }
  retrieve(id: any): Observable<User> {
    // todo test this zip
    return zip(
      this.userSvc.getUser(this.domain, id),
      this.userSvc
        .getUserRoles(this.domain, id)
        .pipe(filter((val) => val != null)),
    ).pipe(
      map((arr: [User, Role[]]) => {
        arr[0].roles = arr[1]
        return arr[0]
      }),
    )
  }

  retrieveId(user: User) {
    return user.id
  }

  doCreate(user: User): Observable<User> {
    const request = {
      username: user.username,
      emailAddress: user.emailAddress,
      domain: user.domain,
      roleIds: user.roles.map((elem) => elem.id),
      sendEmail: true,
      theme: user.theme,
    }
    return this.userSvc.postUser(user.domain, request)
  }

  /**
   * Roles always go to CURO. Username/e-mail only go out for an account
   * CURO still owns; 2FA goes out regardless, since the backend accepts
   * `mfa` on the profile PUT even for a Keycloak-owned account.
   *
   * Keycloak owns the profile of a migrated user, so
   * `PUT /Domains/{domain}/Users/{id}` comes back 400 "not allowed to change
   * user details" for one when it touches username/e-mail. That used to
   * fire anyway, next to the roles PUT that succeeded, so a role change
   * saved and toasted an error at the same time. For a migrated user we
   * send the roles alone UNLESS `mfa` actually changed, in which case we
   * still send the profile PUT (username/e-mail come back unchanged from
   * the read-only form fields, so the 400 doesn't fire).
   *
   * The flag is read off the LOADED user rather than `user`: the detail
   * screen builds its payload field by field and does not echo server-owned
   * fields back, so the argument never carries it. For the same reason the
   * migrated branch answers with the loaded user merged under the edit,
   * or `keycloakMigrated` would drop out of the cached item on every save.
   */
  doUpdate(id: any, user: User): Observable<User> {
    const request = {
      roleIds: user.roles.map((elem) => elem.id),
    }
    return zip(
      // set the roles on the user domain, to make sure the roles are set properly
      this.userSvc.putUser(this.domain, id, user),
      this.userSvc.putUserRoles(user.domain, id, request),
      (user, roles) => {
        user.roles = roles
        return user
      },
    )
  }

  doDelete(id: any): Observable<void> {
    return this.userSvc.deleteUser(this.domain, id)
  }
}
